import { useEffect, useRef } from "react";
import QRCode from "qrcode";
import { Download, ArrowUpRight, Smartphone } from "lucide-react";
import { APP_DOWNLOAD_URL, APP_SIZE, APP_VERSION } from "../config";
import { useSmoothScroll } from "./SmoothScroll";
import { Reveal } from "./Reveal";
import PhoneMockup from "./PhoneMockup";

export default function DownloadCTA() {
  const scrollTo = useSmoothScroll();
  const qrRef = useRef(null);

  useEffect(() => {
    if (qrRef.current) {
      QRCode.toCanvas(qrRef.current, `${window.location.origin}${APP_DOWNLOAD_URL}`, {
        width: 132,
        margin: 0,
        color: { dark: "#08080A", light: "#FFFFFF" },
      });
    }
  }, []);

  return (
    <section id="download" className="relative overflow-hidden py-28 sm:py-36 border-t border-white/[0.06]" data-testid="download-cta-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(212,175,55,0.14),transparent_65%)]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 bg-grid-faint [mask-image:radial-gradient(ellipse_at_bottom,black_20%,transparent_70%)]" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="text-center lg:text-left">
          <Reveal>
            <div className="flex items-center justify-center lg:justify-start gap-3">
              <span className="h-px w-8 bg-[#D4AF37]/60" aria-hidden="true" />
              <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">Get the App</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              Your Financial Future. <span className="text-gold-gradient">One App.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mx-auto lg:mx-0 mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#99A1AF]">
              Download Winter Protocol and explore the growing AI-powered digital wealth ecosystem.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-4">
              <a
                href={APP_DOWNLOAD_URL}
                download="winter-protocol.apk"
                data-testid="cta-download-app-button"
                className="group inline-flex items-center gap-2.5 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-semibold text-[#08080A] transition-all duration-300 hover:bg-[#F3E5AB] hover:shadow-[0_0_50px_rgba(212,175,55,0.4)]"
              >
                <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                Download App
              </a>
              <button
                onClick={() => scrollTo?.("#home")}
                data-testid="cta-get-started-button"
                className="group inline-flex items-center gap-2.5 rounded-full border border-white/20 px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:border-white/50 hover:bg-white/5"
              >
                Get Started
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
            <p className="mt-5 font-mono text-[11px] tracking-[0.22em] text-[#636B78] uppercase" data-testid="app-version-badge">
              v{APP_VERSION} · {APP_SIZE} · Android APK
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mx-auto lg:mx-0 mt-6 flex max-w-md items-start gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4 text-left">
              <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
              <p className="text-xs leading-relaxed text-[#99A1AF]" data-testid="apk-install-hint">
                The Winter Protocol APK is distributed directly through this website. Download the APK
                and install it on your device — you may need to allow installation from unknown sources
                in your Android settings.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.36}>
            <div
              className="mx-auto lg:mx-0 mt-6 flex max-w-md items-center gap-5 rounded-2xl border border-[#D4AF37]/25 bg-[#14141A]/80 p-4 text-left"
              data-testid="qr-download-card"
            >
              <div className="shrink-0 rounded-xl bg-white p-2.5">
                <canvas ref={qrRef} data-testid="qr-download-code" aria-label="QR code to download the Winter Protocol app" />
              </div>
              <div>
                <p className="font-display text-sm font-semibold text-white">Scan to install on Android</p>
                <p className="mt-1.5 text-xs leading-relaxed text-[#99A1AF]">
                  Point your phone camera at the code to download the APK directly from this website.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="mx-auto">
          <div className="relative">
            <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.16),transparent_65%)]" aria-hidden="true" />
            <PhoneMockup />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
